import { useCallback, useEffect } from "react";
import { StatusLogDataWithId } from "../../data-model/statusLogModel";
import { singleOrderDetailsGenericCallForPostgres } from "../../../shared/api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { SingleOrderDetailsTableName } from "../../../shared/enums/singleOrderDetailsTableName";
import { useAbortableFetch } from "../useAbortableFetch";

/** Parameters for the {@link useCommentsData} hook. */
interface UseCommentsDataProps {
  /** PA order ID to fetch comments for. */
  orderId: string;
  /** Organization ID scoping the query. */
  orgId: string;
}

/** Response shape returned by the order-comments Postgres endpoint. */
interface CommentsDataResponse {
  /** List of comment entries for the requested order. */
  comments: StatusLogDataWithId[];
}

/**
 * Fetches comments data for a single medical PA order from Postgres.
 *
 * @param props - The order ID and org ID to fetch comments for
 * @returns The fetched data, loading state, and any error
 */
export const useCommentsData = (props: UseCommentsDataProps) => {
  const { orderId, orgId } = props;

  const fetchCommentsData = useCallback(
    async (signal: AbortSignal) => {
      return singleOrderDetailsGenericCallForPostgres<CommentsDataResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.ORDER_COMMENTS,
        },
        signal,
      );
    },
    [orderId, orgId],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch(fetchCommentsData);

  useEffect(() => {
    if (orderId && orgId) {
      runFetch();
    }

    return () => abort();
  }, [orderId, orgId, runFetch, abort]);

  return { data, loading, error };
};
